/* istanbul ignore file */

import React from 'react';
import { Provider } from 'react-redux';
import type { FeatureFlags } from '@noths/polaris-client-feature-flags';
import { setLocaleCookies } from '@noths/polaris-client-localisation';
import { ErrorBoundary, getLayout, LayoutTypes } from '@noths/polaris-client-next-components';
import {
  FontFaces as LegacyFontFaces,
  Palette as LegacyPalette,
} from '@noths/polaris-client-ribbons-base';
import type { SiteChromeContent } from '@noths/polaris-client-site-chrome-ui';
import { SkipLinks, useSmartAppBanner } from '@noths/polaris-client-site-chrome-ui';
import { FavouritesContainer } from '@noths/polaris-client-user-favourites';
import type { NextConfig as FavouritesNextConfig } from '@noths/polaris-client-user-favourites/src/types/nextConfig';
import { GlobalStyles } from '@noths/polaris-design-system-theme-brand2026customer';
import type { Merge } from '@noths/polaris-dev-ts-recipes';
import type { AppContextPlusCookies } from '@noths/polaris-dev-ts-types';
import type { Store } from '@reduxjs/toolkit';
import { ConnectedRouter } from 'connected-next-router';
import type { AppInitialProps, AppProps } from 'next/app';
import NextApp from 'next/app';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import Head from 'next/head';

import { DiscountPresentationModeProvider } from 'src/context/discountPresentationMode/DiscountPresentationModeContext';
import { isProd, isProdLike } from 'src/environment';
import { getFeatureFlagsWithMatchers } from 'src/featureFlags/getFeatureFlagsWithMatchers';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { useStore } from 'src/redux/store';
import type { NextConfig } from 'src/types/nextConfig';
import type { PageProps } from 'src/types/page';

export const CONTENT_SECTION_ID = 'product-details__content';

const BrowserRum = dynamic(() =>
  import(
    /* webpackChunkName: "datadog_browser_rum" */ '@noths/polaris-client-next-components/src/monitoring/BrowserRum'
  ).then((mod) => mod.BrowserRum),
);

const BrowserLogs = dynamic(() =>
  import(
    /* webpackChunkName: "datadog_browser_logs" */ '@noths/polaris-client-next-components/src/monitoring/BrowserLogs'
  ).then((mod) => mod.BrowserLogs),
);

const nextConfig = getConfig() as NextConfig;
const favouritesNextConfig = nextConfig as unknown as FavouritesNextConfig;
const { publicRuntimeConfig } = nextConfig;
const { version } = publicRuntimeConfig;

type CustomAppProps = Merge<
  AppProps & {
    experiments?: string;
    featureFlags?: FeatureFlags;
    layoutType?: LayoutTypes;
    requestIsFromBot?: boolean;
    searchedTerm?: string;
    siteChromeCompactMode?: boolean;
    siteChromeContent?: SiteChromeContent | null;
    stickyTopBanner?: boolean;
  },
  {
    mvtAssignmentsHeader?: string;
    pageProps: PageProps;
  }
>;

const App = ({ experiments: _experiments, ...props }: CustomAppProps) => {
  const { externalReferrer, updatedReduxState } = props.pageProps || {};
  const store = useStore(props, updatedReduxState as ReduxApplicationState);

  useSmartAppBanner({ hide: externalReferrer });

  const { ENABLE_LOGGING, NODE_ENV, monitoring } = publicRuntimeConfig;

  const errorBoundaryProps = {
    env: NODE_ENV,
    isLoggingEnabled: ENABLE_LOGGING,
  };

  const defaultLayoutType = LayoutTypes.CLIENT_SITE_CHROME_UI;
  const siteChromeContent = props.pageProps?.siteChromeContent ?? props.siteChromeContent;
  const stickyTopBanner = props.stickyTopBanner;
  const layoutType = props.layoutType ?? defaultLayoutType;
  const LayoutComponent = getLayout(layoutType);
  const enableMonitoring = monitoring?.applicationId && isProd();

  return (
    <>
      <SkipLinks links={[{ content: 'Skip to content', sectionId: CONTENT_SECTION_ID }]} />
      <Provider store={store as Store}>
        <ConnectedRouter>
          <FavouritesContainer nextConfig={favouritesNextConfig} />
          <Head>
            <meta content="width=device-width, initial-scale=1" name="viewport" />
          </Head>
          {enableMonitoring && !props.requestIsFromBot && (
            <BrowserRum
              applicationId={monitoring?.applicationId}
              clientToken={monitoring?.clientToken}
              featureFlags={props.featureFlags}
              monitoringEnv={monitoring?.monitoringEnv}
              rumSampleRate={monitoring?.rumSampleRate}
              rumTracingURLs={monitoring?.rumTracingURLs}
              serviceName={monitoring?.serviceName}
              version={version}
            />
          )}
          {enableMonitoring && (
            <BrowserLogs
              clientToken={monitoring?.clientToken}
              forwardConsoleLogs={monitoring.forwardConsoleLogs}
              logsLoggerLevel={monitoring?.logsLoggerLevel}
              logsSampleRate={monitoring?.logsSampleRate}
              monitoringEnv={monitoring?.monitoringEnv}
              serviceName={monitoring?.serviceName}
              version={version}
            />
          )}
          <LegacyFontFaces />
          <LegacyPalette />
          <GlobalStyles />
          <LayoutComponent
            NODE_ENV={NODE_ENV}
            featureFlags={props.featureFlags}
            isProdLikeEnv={isProdLike()}
            mergeBasket={undefined}
            searchedTerm={props.searchedTerm}
            siteChromeCompactMode={props.siteChromeCompactMode ?? false}
            siteChromeContent={siteChromeContent ?? undefined}
            stickyTopBanner={stickyTopBanner}
          >
            <ErrorBoundary {...errorBoundaryProps}>
              <DiscountPresentationModeProvider mode={siteChromeContent?.discountPresentationMode}>
                <props.Component {...props.pageProps} />
              </DiscountPresentationModeProvider>
            </ErrorBoundary>
          </LayoutComponent>
        </ConnectedRouter>
      </Provider>
    </>
  );
};

/*
  Necessary to disable next's Automatic Static Optimization feature.
  Without it this page will be statically compiled.
*/
App.getInitialProps = async (
  appContext: AppContextPlusCookies,
): Promise<AppInitialProps & Pick<CustomAppProps, 'featureFlags'>> => {
  const {
    ctx: { query, req, res },
  } = appContext;
  const featureFlags = (req && getFeatureFlagsWithMatchers(req)) || {};

  if (req && res) {
    setLocaleCookies({ req, res, query });
  }

  const appProps = await NextApp.getInitialProps(appContext);

  return { ...appProps, featureFlags };
};

export default App;
